'use client'

import { LineDownload } from '@qr-front/icons/line-download'
import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogDescription,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { DialogContent, DialogFooter } from '@qr-front/ui/components/Dialog'
import { useTranslations } from 'next-intl'

import { BulkDownloadState } from './useBulkDownload'

type BulkDownloadModalProps = {
	isOpen: boolean
	downloadState: BulkDownloadState
	onClose: () => void
	onCancel: () => void
}

export const BulkDownloadModal = ({
	isOpen,
	downloadState,
	onClose,
	onCancel,
}: BulkDownloadModalProps) => {
	const t = useTranslations()

	const handleClose = () => {
		if (downloadState.isDownloading) {
			onCancel()
		} else {
			onClose()
		}
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="w-full p-6 tablet:w-[500px] tablet:p-8"
			>
				<div className="flex flex-col items-center gap-2">
					<div className="mb-2 flex items-center gap-3">
						<LineDownload className="text-blue-600 size-6" />
						<DialogTitle className="text-center">
							{downloadState.isDownloading
								? t('dashboard__bulk_download__title_downloading')
								: t('dashboard__bulk_download__title_complete')}
						</DialogTitle>
					</div>

					<DialogDescription className="text-center">
						{downloadState.progress || t('dashboard__bulk_download__preparing')}
					</DialogDescription>

					{downloadState.isDownloading && (
						<div className="mt-2 flex items-center gap-2">
							<div className="border-blue-600 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
							<DialogDescription className="text-sm">
								{t('dashboard__bulk_download__processing')}
							</DialogDescription>
						</div>
					)}

					{downloadState.isComplete && !downloadState.isDownloading && (
						<DialogDescription className="text-green-600 text-center font-medium">
							{t('dashboard__bulk_download__success')}
						</DialogDescription>
					)}
				</div>

				<DialogFooter className="mt-6 gap-4">
					<Button onClick={handleClose}>
						{downloadState.isDownloading
							? t('ui__generic__cancel')
							: t('ui__popup__close_button')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
